import { useEffect, useState } from "react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { FloppyDisk, Copy } from "@phosphor-icons/react";
import { copyText } from "@/lib/clipboard";

const FIELDS = [
  ["title", "Title (Mr / Ms / Mx / Dr…)"],
  ["full_name", "Full legal name"],
  ["first_name", "First name"],
  ["middle_names", "Middle name(s)"],
  ["last_name", "Last name / Surname"],
  ["other_names", "Other names / previously known as (optional)"],
  ["preferred_name", "Preferred name / nickname (optional)"],
  ["email", "Email"],
  ["phone", "Phone"],
  ["location", "Location"],
  ["headline", "Headline (one-liner)"],
  ["linkedin", "LinkedIn URL"],
  ["website", "Website"],
  ["portfolio", "Portfolio URL"],
];

function copy(text, label = "Copied") { return copyText(text, label); }

export default function Profile() {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/profile");
      setForm(data?.personal_info || {});
    })();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/profile", form);
      const { data } = await api.get("/profile");
      setForm(data?.personal_info || {});
      toast.success("Profile locked in");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <AppShell title="Profile">
      <p className="text-[var(--brand-muted)] mb-8 max-w-2xl">
        the basics — name, contact details, links. used to autofill the top of any application form.
        for the <b>full block</b> (visa, salary, notice, driving, security, etc.) head to <b>Vault → Personal</b>.
        sensitive items (NI/SSN, equal opps) live in their own tabs.
      </p>

      <div className="mb-6 card-hard rounded-md p-4 bg-[var(--pastel-yellow)] max-w-3xl">
        <p className="label-tiny mb-1">/name splitting</p>
        <p className="text-sm text-[var(--brand-ink)]/85 leading-relaxed">
          different forms ask for names in different formats — sometimes just <b>Full Name</b>, sometimes <b>First / Middle / Last</b>,
          sometimes <b>Surname / Given Name</b>, and some ask for <b>Other Names</b> or a <b>Preferred Name</b>.
          fill your legal name in <b>Full legal name</b> and we&apos;ll auto-split into first / middle / last for you. Edit any of those
          separately if the split isn&apos;t quite right (double-barrelled surnames, saints&apos; names, etc.) — the extension will
          use whichever value the form is actually asking for.
        </p>
      </div>

      <form onSubmit={save} className="card-hard rounded-md p-6 bg-white max-w-3xl" data-testid="profile-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FIELDS.map(([k, label]) => (
            <div key={k}>
              <label className="label-tiny mb-1.5 flex items-center justify-between">
                <span>{label}</span>
                {form[k] && (
                  <button type="button" onClick={() => copy(String(form[k]))}
                    data-testid={`copy-profile-${k}`}
                    className="text-[10px] underline text-[var(--brand-muted)] hover:text-[var(--brand-ink)] flex items-center gap-1">
                    <Copy size={10}/> copy
                  </button>
                )}
              </label>
              <input data-testid={`profile-${k}`} value={form[k] || ""}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className="w-full card-hard rounded-md px-3 py-2 focus:ring-2 focus:ring-[var(--brand-primary)]"/>
            </div>
          ))}
          <div className="md:col-span-2">
            <label className="label-tiny mb-1.5 flex items-center justify-between">
              <span>Summary</span>
              {form.summary && (
                <button type="button" onClick={() => copy(form.summary)} data-testid="copy-profile-summary"
                  className="text-[10px] underline text-[var(--brand-muted)] hover:text-[var(--brand-ink)] flex items-center gap-1">
                  <Copy size={10}/> copy
                </button>
              )}
            </label>
            <textarea data-testid="profile-summary" value={form.summary || ""}
              onChange={(e) => setForm({ ...form, summary: e.target.value })}
              placeholder="a short personal pitch — what you do, what you want."
              className="w-full card-hard rounded-md px-3 py-2 min-h-[120px] focus:ring-2 focus:ring-[var(--brand-primary)]"/>
          </div>
        </div>
        <button type="submit" disabled={saving} data-testid="profile-save"
          className="mt-6 btn-hard bg-[var(--brand-primary)] text-white rounded-md px-4 py-2.5 text-sm flex items-center gap-2 hover:bg-[var(--brand-primary)]">
          <FloppyDisk size={16} weight="bold"/> {saving ? "saving…" : "save profile"}
        </button>
      </form>
    </AppShell>
  );
}
